import { styled } from '@linaria/react'
import React, { type ComponentProps, type FC, type ReactNode } from 'react'
import { colors } from '../styles/colors'
import { fontFamilyMapCss, Typography } from './Typography'
import { cx } from '@linaria/core'

type TagVariant = 'default' | 'primary' | 'success' | 'grey' | 'transparent'

export const Tag: FC<{
  label: ReactNode
  isActive?: boolean
  onClick?: () => void
  icon?: ReactNode
  isNotInteractive?: boolean
  variant?: TagVariant
  className?: string
  zIndex?: number
  typographyProps?: ComponentProps<typeof Typography>
}> = ({
  className,
  zIndex,
  icon,
  isActive = false,
  label,
  onClick,
  variant = 'default',
  isNotInteractive = false,
  typographyProps = {
    fontSize: 14,
    fontWeight: 700,
    color: colors.iconInactive,
  },
}) => {
  const textColor = isActive ? colors.white : typographyProps.color

  return (
    <StyledTag
      zIndex={zIndex}
      isNotInteractive={isNotInteractive}
      className={cx(fontFamilyMapCss.manrope, className)}
      isActive={isActive}
      onClick={onClick}
      type="button"
      variant={variant}
      hasIcon={Boolean(icon)}
    >
      {icon}

      <Typography {...typographyProps} color={textColor}>
        {label}
      </Typography>
    </StyledTag>
  )
}

const borderColor: Record<TagVariant, string> = {
  default: colors.secondary,
  grey: colors.grey300,
  primary: colors.primary,
  success: colors.success,
  transparent: colors.shadowMedium,
}

const borderActiveColor: Record<TagVariant, string> = {
  default: colors.secondary,
  grey: colors.grey500,
  primary: colors.primaryActive,
  success: colors.successActive,
  transparent: colors.shadowMedium,
}

const backgroundColor: Record<TagVariant, string> = {
  default: colors.bgAlt,
  grey: colors.grey300,
  primary: colors.warning,
  success: colors.success,
  transparent: colors.shadowMedium,
}

const backgroundActiveColor: Record<TagVariant, string> = {
  default: colors.secondary,
  grey: colors.grey500,
  primary: colors.primaryActive,
  success: colors.successActive,
  transparent: colors.shadowMedium,
}

const borderHoverColor: Record<TagVariant, string> = {
  default: colors.secondaryLight,
  grey: colors.grey200,
  primary: colors.primaryLight,
  success: colors.successLight,
  transparent: colors.shadowMedium,
}

const backgroundHoverColor: Record<TagVariant, string> = {
  default: colors.secondaryClarity,
  grey: colors.grey200,
  primary: colors.primaryClarity,
  success: colors.successClarity,
  transparent: colors.shadowMedium,
}

const StyledTag = styled.button<{
  isActive: boolean
  variant: TagVariant
  hasIcon: boolean
  isNotInteractive?: boolean
  zIndex?: number
}>`
  z-index: ${({ zIndex }) => zIndex ?? 'auto'};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px 16px;
  gap: ${props => (props.hasIcon ? '4px' : '0')};
  border-radius: 100px;
  pointer-events: ${props => (props.isNotInteractive ? 'none' : 'auto')};

  border: 1px solid
    ${props =>
      props.isActive
        ? borderActiveColor[props.variant]
        : borderColor[props.variant]};

  background-color: ${props =>
    props.isActive
      ? backgroundActiveColor[props.variant]
      : backgroundColor[props.variant]};

  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => {
      return props.isActive
        ? backgroundHoverColor[props.variant]
        : backgroundColor[props.variant]
    }};

    border-color: ${props => {
      return props.isActive
        ? borderHoverColor[props.variant]
        : borderColor[props.variant]
    }};
  }

  &:active {
    transform: scale(0.98);
  }
`
